import { useNavigate, useParams } from 'react-router-dom';
import { useTVGuide, useUpdateTVGuide, useTranslation } from '@/hooks';
import { CreateTVGuideRequest } from '@/types/model';
import TVGuideForm from '@/components/forms/TVGuideForm';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout.tsx';

const EditTVGuide = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const { data: tvGuide, isLoading, isError, error } = useTVGuide(id!);
  const updateTVGuideMutation = useUpdateTVGuide();

  const handleSuccess = async (data: CreateTVGuideRequest) => {
    if (!id) return;

    try {
      await updateTVGuideMutation.mutateAsync({ id, data });
      toast.success(t.tvGuide.messages.updated);
      navigate('/content/tv-channels/tv-guide');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t.errors.unexpectedError);
      throw err;
    }
  };

  const handleCancel = () => {
    navigate('/content/tv-channels/tv-guide');
  };

  if (isLoading) {
    return (
      <TVChannelsLayout>
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-gray-400 mr-2"/>
                <span className="text-gray-500">Cargando guía de TV...</span>
              </div>
            </div>
          </div>
        </div>
      </TVChannelsLayout>
    );
  }

  if (isError || !tvGuide) {
    return (
      <TVChannelsLayout>
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-center py-12">
                <div className="text-red-600 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Error al cargar la guía de TV</h3>
                <p className="text-gray-500 mb-4">{error?.message || 'La guía de TV no fue encontrada'}</p>
                <button
                  onClick={() => navigate('/content/tv-channels/tv-guide')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Volver a Guías de TV
                </button>
              </div>
            </div>
          </div>
        </div>
      </TVChannelsLayout>
    );
  }

  return (
    <TVChannelsLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <TVGuideForm
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            initialData={tvGuide}
            isEdit={true}
            isSubmitting={updateTVGuideMutation.isPending}
          />
        </div>
      </div>
    </TVChannelsLayout>
  );
};

export default EditTVGuide;
